import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { Button, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { OrderStatus, SellerOrderListItem, ShipmentStatus } from "@/lib/api/types";
import { useOrdersQuery } from "@/modules/orders/services/useOrdersQuery";
import type {
  OrdersQueryPaymentMethod,
  OrdersQuerySort,
} from "@/modules/orders/services/useOrdersQuery";
import { useUpdateOrderStatusMutation } from "@/modules/orders/services/useUpdateOrderStatusMutation";
import { useOrderStatusCountsQuery } from "@/modules/orders/services/useOrderStatusCountsQuery";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useLocale } from "@seller/app/locale";
import { getBuyerAbsoluteUrl } from "@/lib/routing/portal-urls";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateSellerOrders } from "@/lib/query/core/invalidation";
import { usePullToRefresh } from "@/shared/pull-to-refresh";
import {
  PageTransition,
  PageTransitionItem,
} from "@/shared/components/PageTransition";
import {
  type OrdersSort,
  type OrdersViewMode,
  type OrdersBucketFilter,
} from "@/modules/orders/components/OrdersHeroFilters";
import { OrdersFilterToolbar } from "@/modules/orders/components/OrdersFilterToolbar";
import { OrdersNeedsAttention } from "@/modules/orders/components/OrdersNeedsAttention";
import {
  OrdersFiltersDrawer,
  type DateRange,
  type PaymentFilter,
} from "@/modules/orders/components/OrdersFiltersDrawer";
import { OrdersTableView } from "@/modules/orders/components/OrdersTableView";
import { OrdersCardsView } from "@/modules/orders/components/OrdersCardsView";
import { MobileOrdersView } from "@/modules/orders/components/mobile/MobileOrdersView";
import { MobileOrderFullScreen } from "@/modules/orders/components/mobile/MobileOrderFullScreen";
import { OrderDetailModal } from "@/modules/orders/components/OrderDetailModal";
import { OrderQuickActionSheet } from "@/modules/orders/components/OrderQuickActionSheet";
import { SMART_BUCKETS_ORDERED, type SmartBucketId } from "@/modules/orders/lib/smart-buckets";
import { isInTopCards } from "@/modules/orders/lib/orders-attention-model";
import { ORDER_STATUS_VALUES } from "@/modules/orders/components/order-status";
import { getOrderActionConfirmCopy } from "@/modules/orders/lib/order-action-confirm";
import { OrderActionConfirm } from "@/modules/orders/components/OrderActionConfirm";
import { useGovernoratesQuery } from "@/modules/settings/services/useGovernoratesQuery";
import { OrdersFilterChip } from "@/modules/orders/components/OrdersFilterChip";
import { OrdersEmptyState } from "@/modules/orders/components/OrdersEmptyState";
import { OrdersFirstRunEmpty } from "@/modules/orders/components/OrdersFirstRunEmpty";
import { IncompleteEmptyState } from "@/modules/orders/components/orders-smart/IncompleteEmptyState";
import { ErrorState } from "@/shared/components/ErrorState";

const PAGE_SIZE = 20;
const VIEW_MODE_KEY = "seller_orders_view_v3";

// URL-param whitelists — unknown values fall back to the default instead of
// being blindly cast (a hand-edited URL must not produce a bogus API filter).
const DATE_RANGE_VALUES: readonly DateRange[] = ["all", "today", "week", "month"];
const PAYMENT_FILTER_VALUES: readonly PaymentFilter[] = ["all", "cod", "online"];
const SHIPMENT_STATUS_VALUES: readonly ShipmentStatus[] = [
  "pending_pickup",
  "picked_up",
  "in_transit",
  "delivered",
  "returned",
  "exception",
  "cancelled",
  "lost",
  "on_hold",
];

// Statuses whose quick-action button means "review the payment proof first".
// Clicking it opens the order detail to review — it must NOT confirm directly.
const REVIEW_PAYMENT_STATUSES: ReadonlySet<OrderStatus> = new Set([
  "pending_payment_verification",
  "paid_less_than_required",
]);

/** Compute ISO dateFrom/dateTo from a DateRange preset */
function computeDateBounds(preset: DateRange): { dateFrom?: string; dateTo?: string } {
  if (preset === "all") return {};
  const now = new Date();
  const endOfDay = new Date(now);
  endOfDay.setHours(23, 59, 59, 999);
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);

  if (preset === "today") {
    return {
      dateFrom: startOfDay.toISOString(),
      dateTo: endOfDay.toISOString(),
    };
  }
  // Inclusive window: "week" = today + 6 prior days (7 days), "month" = today + 29 (30 days).
  const daysBack = preset === "week" ? 6 : 29;
  const fromDate = new Date(startOfDay);
  fromDate.setDate(fromDate.getDate() - daysBack);
  return {
    dateFrom: fromDate.toISOString(),
    dateTo: endOfDay.toISOString(),
  };
}

/** Map PaymentFilter UI value → backend paymentMethod enum */
function toBackendPaymentMethod(payment: PaymentFilter): OrdersQueryPaymentMethod | undefined {
  if (payment === "cod") return "COD";
  if (payment === "online") return "EXTERNAL_ONLINE";
  return undefined;
}

/** Convert a local YYYY-MM-DD string to a start-of-day ISO string */
function localDateToISOFrom(dateStr: string): string | undefined {
  if (!dateStr) return undefined;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Convert a local YYYY-MM-DD string to an end-of-day ISO string */
function localDateToISOTo(dateStr: string): string | undefined {
  if (!dateStr) return undefined;
  const d = new Date(dateStr + "T23:59:59.999");
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

export function OrdersPage() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  // Pull-to-refresh (mobile PWA): invalidate every order query (list, infinite
  // "smart" view, and status counts) so the refresh is view-agnostic. Awaited
  // so the spinner stays until the active query settles.
  usePullToRefresh(() => invalidateSellerOrders(queryClient, storeSlug));

  // ── URL-backed filter state ──────────────────────────────────────────────
  // URL keys:
  //   bucket=<SmartBucketId|"all">     (Phase 4 — replaces old ?status=)
  //   refStatus=<csv-of-OrderStatus>    (Phase 4.2 — drawer refinement within bucket)
  //   sort=<newest|oldest>              (existing)
  //   date=<all|today|week|month>       (existing preset)
  //   pay=<all|cod|online>              (existing — payment METHOD)
  //   page=<n>                          (existing)
  //   order=<id>                        (existing — modal)
  //   gov=<csv-of-uuids>                (Phase 4 — governorate filter)
  //   from=<YYYY-MM-DD>                 (Phase 4 — custom date from, overrides preset)
  //   to=<YYYY-MM-DD>                   (Phase 4 — custom date to, overrides preset)
  //   ship=<shipment status value>      (Phase 4 — shipment status)
  const [searchParams, setSearchParams] = useSearchParams();

  const bucketParam = searchParams.get("bucket") ?? "all";
  const activeBucket: OrdersBucketFilter =
    (SMART_BUCKETS_ORDERED as SmartBucketId[]).includes(bucketParam as SmartBucketId)
      ? (bucketParam as SmartBucketId)
      : bucketParam === "incomplete"
        ? "incomplete"
        : "all";

  // Phase 4.2 — drawer status refinement (subset of the active bucket's statuses)
  const refStatusParam = searchParams.get("refStatus") ?? "";
  const refinedStatuses: OrderStatus[] = useMemo(
    () =>
      refStatusParam
        ? refStatusParam
            .split(",")
            .filter((s): s is OrderStatus =>
              (ORDER_STATUS_VALUES as string[]).includes(s),
            )
        : [],
    [refStatusParam],
  );

  const sortParamRaw = searchParams.get("sort");
  const sort: OrdersSort = sortParamRaw === "oldest" ? "oldest" : "newest";
  const dateRangeRaw = searchParams.get("date") ?? "all";
  const dateRange: DateRange = (DATE_RANGE_VALUES as string[]).includes(dateRangeRaw)
    ? (dateRangeRaw as DateRange)
    : "all";
  const paymentRaw = searchParams.get("pay") ?? "all";
  const payment: PaymentFilter = (PAYMENT_FILTER_VALUES as string[]).includes(paymentRaw)
    ? (paymentRaw as PaymentFilter)
    : "all";

  // Phase 4 params
  const govParam = searchParams.get("gov") ?? "";
  const selectedGovernorates = useMemo(
    () => (govParam ? govParam.split(",").filter(Boolean) : []),
    [govParam],
  );
  const customDateFrom = searchParams.get("from") ?? "";
  const customDateTo = searchParams.get("to") ?? "";
  const shipmentStatusRaw = searchParams.get("ship") ?? "all";
  const shipmentStatus: ShipmentStatus | "all" = (
    SHIPMENT_STATUS_VALUES as string[]
  ).includes(shipmentStatusRaw)
    ? (shipmentStatusRaw as ShipmentStatus)
    : "all";

  // ── Governorates reference data ──────────────────────────────────────────
  const { data: governoratesData } = useGovernoratesQuery({ storeSlug });
  const allGovernorates = governoratesData ?? [];

  const setBucket = useCallback(
    (next: OrdersBucketFilter) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "all") {
            p.delete("bucket");
          } else {
            p.set("bucket", next);
          }
          // Changing the tab bucket resets refinement + pagination
          p.delete("refStatus");
          p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRefinedStatuses = useCallback(
    (statuses: OrderStatus[]) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (statuses.length === 0) {
            p.delete("refStatus");
          } else {
            p.set("refStatus", statuses.join(","));
          }
          p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSort = useCallback(
    (next: OrdersSort) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "newest") {
            p.delete("sort");
          } else {
            p.set("sort", next);
          }
          p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setPayment = useCallback(
    (next: PaymentFilter) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === "all") {
            p.delete("pay");
          } else {
            p.set("pay", next);
          }
          p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Phase 4 setters
  const setGovernorates = useCallback(
    (ids: string[]) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (ids.length === 0) {
            p.delete("gov");
          } else {
            p.set("gov", ids.join(","));
          }
          p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setShipmentStatus = useCallback(
    (v: ShipmentStatus | "all") => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (v === "all") {
            p.delete("ship");
          } else {
            p.set("ship", v);
          }
          p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // ── Pagination (URL-backed via ?page=, 1-indexed) ────────────────────────
  const pageNum = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const offset = (pageNum - 1) * PAGE_SIZE;

  const goToPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (page <= 1) {
            p.delete("page");
          } else {
            p.set("page", String(page));
          }
          return p;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  // ── View mode (localStorage-persisted, not URL) ──────────────────────────
  const [viewMode, setViewMode] = useState<OrdersViewMode>(() => {
    if (typeof window === "undefined") return "smart";
    const stored = localStorage.getItem(VIEW_MODE_KEY);
    return stored === "cards" || stored === "table" || stored === "smart" ? stored : "smart";
  });
  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  // ── Drawer open state ────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── ?order= detail modal (pre-existing URL contract — preserved) ─────────
  const selectedOrderId = searchParams.get("order");
  const openOrder = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("order", id);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const closeOrder = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("order");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // ── Status counts (for tab pills + drawer refinement) ───────────────────
  const { data: statusCountsData } = useOrderStatusCountsQuery({ storeSlug });
  const totalAll = statusCountsData?.total ?? 0;
  // First run = the store has no orders at all (not just an empty filter).
  const isFirstRun = statusCountsData !== undefined && totalAll === 0;
  const storeUrl = getBuyerAbsoluteUrl(locale, storeSlug);

  // Draft count (live drafts only — expired drafts become cancelled)
  const draftCount = statusCountsData?.counts.draft ?? 0;

  // Build a count map for the 4 buckets + "all" + "incomplete"
  const bucketCountMap = useMemo<Record<SmartBucketId | "all" | "incomplete", number>>(() => {
    const base: Record<SmartBucketId | "all" | "incomplete", number> = {
      all: 0,
      needs_attention: 0,
      waiting_on_buyer: 0,
      in_fulfillment: 0,
      shipped: 0,
      closed: 0,
      incomplete: draftCount,
    };
    for (const b of statusCountsData?.buckets ?? []) {
      // Whitelist bucket ids from the API response — skip unknown values
      // instead of writing arbitrary keys into the map.
      if ((SMART_BUCKETS_ORDERED as string[]).includes(b.id)) {
        base[b.id as SmartBucketId] = b.count;
      }
    }
    // "All" lists submitted orders that AREN'T in the always-on top cards, i.e.
    // everything except drafts, needs_attention and waiting_on_buyer.
    base.all = Math.max(
      0,
      totalAll - draftCount - base.needs_attention - base.waiting_on_buyer,
    );
    return base;
  }, [statusCountsData?.buckets, totalAll, draftCount]);

  // The needs_attention count for the header badge
  const actionNeededCount = bucketCountMap.needs_attention;

  // ── Derive API status filter for Cards/Table view ────────────────────────
  // In Cards/Table, the bucket tab expands to its member statuses via
  // the status-counts response (additive Phase 4.0 field). Before the
  // response arrives, fall back to undefined (all orders).
  const statusParam: OrderStatus[] | undefined = useMemo(() => {
    if (activeBucket === "incomplete") return ["draft"] as OrderStatus[];
    if (activeBucket === "all") return undefined;
    // If drawer refinement is active, use refined subset (always within the bucket)
    if (refinedStatuses.length > 0) return refinedStatuses;
    // Expand bucket → member statuses from the API response
    const bucketData = statusCountsData?.buckets?.find((b) => b.id === activeBucket);
    if (bucketData?.statuses && bucketData.statuses.length > 0) {
      return bucketData.statuses;
    }
    return undefined;
  }, [activeBucket, refinedStatuses, statusCountsData?.buckets]);

  const sortParam: OrdersQuerySort | undefined =
    sort === "newest" ? undefined : (sort as OrdersQuerySort);

  // Custom date range overrides preset when both from+to are set
  const hasCustomDate = Boolean(customDateFrom && customDateTo);
  const { dateFrom: presetDateFrom, dateTo: presetDateTo } = computeDateBounds(
    hasCustomDate ? "all" : dateRange,
  );
  const dateFrom = hasCustomDate
    ? localDateToISOFrom(customDateFrom)
    : presetDateFrom;
  const dateTo = hasCustomDate
    ? localDateToISOTo(customDateTo)
    : presetDateTo;

  const paymentMethodParam = toBackendPaymentMethod(payment);
  const shipmentStatusParam: string | undefined =
    shipmentStatus !== "all" ? shipmentStatus : undefined;
  const governorateIdsParam: string[] | undefined =
    selectedGovernorates.length > 0 ? selectedGovernorates : undefined;

  // ── Server query (Cards/Table only — Smart view manages its own queries) ─
  // Desktop follows the Trioty design: the table/card list with an inline
  // toolbar (no standalone "smart" mode); mobile keeps the smart view.
  const desktopViewMode: "table" | "cards" =
    viewMode === "cards" ? "cards" : "table";
  const effectiveViewMode: OrdersViewMode = isMobile ? "smart" : desktopViewMode;

  const { data: ordersData, isLoading, error: ordersError, refetch: refetchOrders } = useOrdersQuery({
    storeSlug,
    limit: PAGE_SIZE,
    offset,
    status: statusParam,
    sort: sortParam,
    dateFrom,
    dateTo,
    paymentMethod: paymentMethodParam,
    governorateIds: governorateIdsParam,
    shipmentStatus: shipmentStatusParam,
    // NOTE: do NOT pass bucket= here for Cards/Table — that would activate
    // keyset pagination and break offset pagination. Bucket filtering is done
    // via the `status` expansion above.
  });

  // Server list drives the view — no client-side filteredOrders / sortedOrders
  const orders = useMemo(() => ordersData?.items ?? [], [ordersData?.items]);
  // The browse table/cards exclude the orders already shown in the always-on
  // top cards (needs_attention + waiting_on_buyer) so they aren't shown twice.
  const listOrders = useMemo(() => orders.filter((o) => !isInTopCards(o.status)), [orders]);
  const serverTotal = ordersData?.total ?? 0;
  const totalPages = Math.ceil(serverTotal / PAGE_SIZE);

  // ── Translated strings ───────────────────────────────────────────────────
  const ordersTitle = t(keys.orders.title);
  const filtersLabel = t(keys.orders.filtersAction);
  const needActionText = t(keys.orders.needActionCount, {
    count: actionNeededCount,
  });
  const subtitleText = t(keys.orders.subtitle, { total: Math.max(0, totalAll - draftCount) });

  // Active filter count now includes bucket refinement + Phase 4 filters
  const activeFilterCount =
    (refinedStatuses.length > 0 ? 1 : 0) +
    (hasCustomDate ? 1 : dateRange !== "all" ? 1 : 0) +
    (payment !== "all" ? 1 : 0) +
    (selectedGovernorates.length > 0 ? 1 : 0) +
    (shipmentStatus !== "all" ? 1 : 0);

  // ── Quick actions ─────────────────────────────────────────────────────────
  const toast = useToast();
  const { mutateAsync: updateOrderStatus } = useUpdateOrderStatusMutation();
  const [quickSheetOrder, setQuickSheetOrder] =
    useState<SellerOrderListItem | null>(null);
  const [quickActionBusyId, setQuickActionBusyId] = useState<string | null>(
    null,
  );
  // Pending quick action awaiting confirmation in the OrderActionConfirm popup.
  const [pendingAction, setPendingAction] = useState<{
    order: SellerOrderListItem;
    targetStatus: OrderStatus;
  } | null>(null);

  const handleQuickAction = useCallback(
    (order: SellerOrderListItem, targetStatus: OrderStatus) => {
      // Review-payment statuses: open the order detail to review the proof
      // instead of confirming straight from the list.
      if (REVIEW_PAYMENT_STATUSES.has(order.status)) {
        openOrder(order.id);
        return;
      }
      // Shipping is a carrier choice (or manual) — route it through the order
      // detail's Ship chooser instead of a plain list-level confirm.
      if (targetStatus === "shipped") {
        openOrder(order.id);
        return;
      }
      // Every other action asks for explicit confirmation first.
      setPendingAction({ order, targetStatus });
    },
    [openOrder, setPendingAction],
  );

  const runQuickAction = useCallback(
    async (etaDays: number | null) => {
      if (!pendingAction) return;
      const { order, targetStatus } = pendingAction;
      setQuickActionBusyId(order.id);
      try {
        await updateOrderStatus({
          storeSlug,
          orderId: order.id,
          status: targetStatus,
          statusEtaDays: etaDays,
        });
        toast.success(t(keys.orders.statusSavedMicro));
      } catch {
        // Error is surfaced globally via the React Query MutationCache handler.
      } finally {
        setQuickActionBusyId(null);
        setPendingAction(null);
      }
    },
    [pendingAction, updateOrderStatus, storeSlug, toast, t],
  );

  // Mark-shipped is the only forward action that collects an (optional) ETA.
  const pendingNeedsEta = pendingAction?.targetStatus === "shipped";
  const pendingCopy = pendingAction
    ? getOrderActionConfirmCopy(pendingAction.targetStatus)
    : null;

  // Reset handler for the drawer — clears ALL Phase 4 filters + refinement.
  const handleDrawerReset = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      ["date", "pay", "gov", "from", "to", "ship", "page", "refStatus"].forEach(
        (k) => p.delete(k),
      );
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // Combined date-preset handler
  const handleDatePresetChange = useCallback(
    (preset: DateRange) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (preset === "all") {
          p.delete("date");
        } else {
          p.set("date", preset);
        }
        p.delete("from");
        p.delete("to");
        p.delete("page");
        return p;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // Combined custom-date handler
  const handleCustomDateRangeChange = useCallback(
    (from: string, to: string) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        if (from) {
          p.set("from", from);
        } else {
          p.delete("from");
        }
        if (to) {
          p.set("to", to);
        } else {
          p.delete("to");
        }
        p.delete("date");
        p.delete("page");
        return p;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // Clear all filters
  const handleClearAll = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      [
        "bucket", "refStatus", "date", "pay", "gov", "from", "to", "ship", "page",
      ].forEach((k) => p.delete(k));
      return p;
    }, { replace: true });
  }, [setSearchParams]);

  // ── The set of statuses the drawer may offer for refinement ─────────────
  // When "all" tab: offer all statuses (no restriction)
  // When a specific bucket: offer only that bucket's member statuses
  const drawerBucketStatuses: OrderStatus[] | undefined = useMemo(() => {
    if (activeBucket === "all" || activeBucket === "incomplete") return undefined; // undefined = offer all (or no refinement)
    const bucketData = statusCountsData?.buckets?.find((b) => b.id === activeBucket);
    return bucketData?.statuses ?? undefined;
  }, [activeBucket, statusCountsData?.buckets]);

  // ── Page chrome (desktop hero) ────────────────────────────────────────────
  const { setHero, setHeroAux } = usePageChrome();

  useEffect(() => {
    if (isMobile) {
      setHero(null);
      return () => setHero(null);
    }
    setHero(
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="text-[28px] font-semibold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
            {ordersTitle}
          </h1>
          <p className="text-[13px] text-[color:var(--text-muted)]">
            {isFirstRun
              ? t(keys.orders.storeLiveSubtitle)
              : actionNeededCount > 0
                ? `${subtitleText} · ${needActionText}`
                : subtitleText}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={() => setDrawerOpen(true)}
            leadingIcon={<FilterIcon />}
            trailingIcon={
              activeFilterCount > 0 ? (
                <span className="inline-flex h-4 min-w-[18px] items-center justify-center rounded-full bg-[color:var(--accent-500)] px-1 font-mono text-[10.5px] font-bold text-[#0b1220]">
                  {activeFilterCount}
                </span>
              ) : undefined
            }
          >
            {filtersLabel}
          </Button>
        </div>
      </div>,
    );
    return () => setHero(null);
  }, [
    setHero,
    isMobile,
    ordersTitle,
    actionNeededCount,
    totalAll,
    activeFilterCount,
    filtersLabel,
    needActionText,
    subtitleText,
    isFirstRun,
    t,
  ]);

  // Filters now live inline in the orders card (Trioty design), so keep the
  // shared hero-aux slot clear.
  useEffect(() => {
    setHeroAux(null);
    return () => setHeroAux(null);
  }, [setHeroAux]);

  const isEmpty = !isLoading && !ordersError && orders.length === 0;
  const hasAnyFilter =
    (activeBucket !== "all" && activeBucket !== "incomplete") ||
    refinedStatuses.length > 0 ||
    dateRange !== "all" ||
    payment !== "all" ||
    selectedGovernorates.length > 0 ||
    hasCustomDate ||
    shipmentStatus !== "all";

  return (
    <>
      {isMobile && effectiveViewMode === "smart" && !isFirstRun ? (
        // Mobile Orders renders full-bleed OUTSIDE PageTransition — the page
        // transition uses a transform, which would break the sticky header.
        // No top padding here: the sticky header owns its own top space so it
        // doesn't visibly travel before pinning.
        <div className="flex flex-col px-4 pb-8">
          <MobileOrdersView
            storeSlug={storeSlug}
            onOpenFull={openOrder}
            onQuickAction={handleQuickAction}
            onOpenQuickActionSheet={setQuickSheetOrder}
          />
        </div>
      ) : (
      <PageTransition className="w-full">

        <div
          className={[
            "flex flex-col gap-5",
            isMobile ? "px-4 pb-8 pt-3.5" : "px-7 py-5",
          ].join(" ")}
        >
          {activeFilterCount > 0 ? (
            <PageTransitionItem>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11.5px] font-medium text-[color:var(--text-muted)]">
                  {t(keys.orders.activeFiltersLabel)}
                </span>
                {/* Status refinement chip */}
                {refinedStatuses.length > 0 ? (
                  <OrdersFilterChip
                    label={t(keys.orders.filterStatusPrefix, {
                      value: refinedStatuses.length === 1
                        ? refinedStatuses[0]!.replace(/_/g, " ")
                        : `${refinedStatuses.length} statuses`,
                    })}
                    onClear={() => setRefinedStatuses([])}
                  />
                ) : null}
                {/* Existing date preset chip */}
                {dateRange !== "all" && !hasCustomDate ? (
                  <OrdersFilterChip
                    label={t(keys.orders.filterDatePrefix, {
                      value:
                        dateRange === "today"
                          ? t(keys.orders.dateToday)
                          : dateRange === "week"
                            ? t(keys.orders.dateThisWeek)
                            : t(keys.orders.dateThisMonth),
                    })}
                    onClear={() => handleDatePresetChange("all")}
                  />
                ) : null}
                {/* Custom date range chip */}
                {hasCustomDate ? (
                  <OrdersFilterChip
                    label={t(keys.orders.filterCustomDatePrefix, {
                      from: customDateFrom,
                      to: customDateTo,
                    })}
                    onClear={() => handleCustomDateRangeChange("", "")}
                  />
                ) : null}
                {/* Payment method chip */}
                {payment !== "all" ? (
                  <OrdersFilterChip
                    label={t(keys.orders.filterPaymentPrefix, {
                      value:
                        payment === "cod"
                          ? t(keys.orders.paymentMethodCod)
                          : t(keys.orders.paymentMethodExternalOnline),
                    })}
                    onClear={() => setPayment("all")}
                  />
                ) : null}
                {/* Governorate chip */}
                {selectedGovernorates.length > 0 ? (
                  <OrdersFilterChip
                    label={t(keys.orders.filterGovPrefix, {
                      count: selectedGovernorates.length,
                    })}
                    onClear={() => setGovernorates([])}
                  />
                ) : null}
                {/* Shipment status chip */}
                {shipmentStatus !== "all" ? (
                  <OrdersFilterChip
                    label={t(keys.orders.filterShipmentPrefix, {
                      value: t(
                        shipmentStatus === "pending_pickup"
                          ? keys.orders.shipmentStatusPendingPickup
                          : shipmentStatus === "picked_up"
                            ? keys.orders.shipmentStatusPickedUp
                            : shipmentStatus === "in_transit"
                              ? keys.orders.shipmentStatusInTransit
                              : shipmentStatus === "delivered"
                                ? keys.orders.shipmentStatusDelivered
                                : shipmentStatus === "returned"
                                  ? keys.orders.shipmentStatusReturned
                                  : shipmentStatus === "exception"
                                    ? keys.orders.shipmentStatusException
                                    : shipmentStatus === "cancelled"
                                      ? keys.orders.shipmentStatusCancelled
                                      : shipmentStatus === "lost"
                                        ? keys.orders.shipmentStatusLost
                                        : keys.orders.shipmentStatusOnHold,
                      ),
                    })}
                    onClear={() => setShipmentStatus("all")}
                  />
                ) : null}
                <button
                  type="button"
                  onClick={handleDrawerReset}
                  className="text-[11.5px] text-[color:var(--text-secondary)] underline"
                >
                  {t(keys.orders.clearAll)}
                </button>
              </div>
            </PageTransitionItem>
          ) : null}

          <PageTransitionItem>
            {isFirstRun ? (
              <OrdersFirstRunEmpty storeUrl={storeUrl} />
            ) : effectiveViewMode === "smart" ? (
              // Mobile: the redesigned Orders screen (tabs, grouping, Display &
              // Filter sheets, two-tier detail). Self-contained — owns its own
              // data/state; the page keeps the full OrderDetailModal, search,
              // and the decline-reason / ETA quick-action sheet.
              <MobileOrdersView
                storeSlug={storeSlug}
                onOpenFull={openOrder}
                onQuickAction={handleQuickAction}
                onOpenQuickActionSheet={setQuickSheetOrder}
              />
            ) : (
              // Desktop: Trioty layout — always-on "Needs attention" card, then
              // one outer card wrapping the filter toolbar, the inner table
              // card, a dashed separator, and the pagination footer.
              <>
                <OrdersNeedsAttention
                  storeSlug={storeSlug}
                  paymentMethod={paymentMethodParam}
                  sort={sort}
                  onSelectOrder={openOrder}
                  onQuickAction={handleQuickAction}
                  onOpenQuickSheet={setQuickSheetOrder}
                  quickActionBusyId={quickActionBusyId}
                />
                <div className="rounded-[20px] border border-[color:var(--line2)] bg-[color:var(--card)] px-4 pt-4 pb-[18px]">
                  <OrdersFilterToolbar
                    filter={activeBucket}
                    onFilterChange={setBucket}
                    counts={bucketCountMap}
                    payment={payment}
                    onPaymentChange={setPayment}
                    sort={sort}
                    onSortChange={setSort}
                    viewMode={desktopViewMode}
                    onViewModeChange={setViewMode}
                  />
                  {ordersError && !isLoading ? (
                    <ErrorState
                      message={t(keys.orders.fetchTimeoutFallback)}
                      onRetry={() => {
                        void refetchOrders();
                      }}
                    />
                  ) : isEmpty && activeBucket === "incomplete" ? (
                    <IncompleteEmptyState />
                  ) : isEmpty ? (
                    <OrdersEmptyState
                      hasFilters={hasAnyFilter}
                      onClear={handleClearAll}
                    />
                  ) : (
                    <>
                      {desktopViewMode === "table" ? (
                        <OrdersTableView
                          orders={listOrders}
                          onSelectOrder={openOrder}
                          onQuickAction={handleQuickAction}
                          onOpenQuickSheet={setQuickSheetOrder}
                          quickActionBusyId={quickActionBusyId}
                        />
                      ) : (
                        <OrdersCardsView
                          orders={listOrders}
                          onSelectOrder={openOrder}
                          onQuickAction={handleQuickAction}
                          onOpenQuickSheet={setQuickSheetOrder}
                          quickActionBusyId={quickActionBusyId}
                        />
                      )}
                      <OrdersTableFooter
                        shown={listOrders.length}
                        total={serverTotal}
                        page={pageNum}
                        totalPages={totalPages}
                        onPage={goToPage}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </PageTransitionItem>

        </div>
      </PageTransition>
      )}

      <OrdersFiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        dateRange={dateRange}
        onDatePresetChange={handleDatePresetChange}
        payment={payment}
        onPaymentChange={setPayment}
        onReset={handleDrawerReset}
        sort={isMobile ? sort : undefined}
        onSortChange={isMobile ? setSort : undefined}
        allGovernorates={allGovernorates}
        selectedGovernorates={selectedGovernorates}
        onGovernoratesChange={setGovernorates}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        onCustomDateRangeChange={handleCustomDateRangeChange}
        shipmentStatus={shipmentStatus}
        onShipmentStatusChange={setShipmentStatus}
        activeBucket={activeBucket}
        bucketStatuses={drawerBucketStatuses}
        refinedStatuses={refinedStatuses}
        onRefinedStatusesChange={setRefinedStatuses}
      />
      {isMobile ? (
        <MobileOrderFullScreen
          storeSlug={storeSlug}
          orderId={selectedOrderId}
          onClose={closeOrder}
        />
      ) : (
        <OrderDetailModal
          storeSlug={storeSlug}
          orderId={selectedOrderId}
          onClose={closeOrder}
        />
      )}
      {pendingAction && pendingCopy ? (
        <OrderActionConfirm
          isOpen
          orderCode={pendingAction.order.orderCode}
          fromStatus={pendingAction.order.status}
          toStatus={pendingAction.targetStatus}
          bodyKey={pendingNeedsEta ? null : pendingCopy.bodyKey}
          confirmKey={pendingCopy.confirmKey}
          needsEta={pendingNeedsEta}
          isSubmitting={quickActionBusyId !== null}
          onConfirm={(eta) => void runQuickAction(eta)}
          onClose={() => setPendingAction(null)}
        />
      ) : null}
      <OrderQuickActionSheet
        orderId={quickSheetOrder?.id ?? null}
        order={quickSheetOrder}
        isOpen={quickSheetOrder !== null}
        storeSlug={storeSlug}
        locale={locale}
        onClose={() => setQuickSheetOrder(null)}
        onSuccess={() => setQuickSheetOrder(null)}
      />
      <Outlet />

    </>
  );
}

function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/** Page numbers for the footer pager — windowed with "…" when there are many. */
function buildPageList(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const wanted = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...wanted]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * Table footer (Trioty design): a masked dashed separator, a "Showing N of M
 * orders" line, and numbered pagination (prev · pages · next).
 */
function OrdersTableFooter({
  shown,
  total,
  page,
  totalPages,
  onPage,
}: {
  shown: number;
  total: number;
  page: number;
  totalPages: number;
  onPage: (p: number) => void;
}) {
  const t = useTranslations();
  const pageBtn =
    "flex h-[34px] min-w-[34px] items-center justify-center rounded-[9px] px-2 text-[13px] font-semibold transition-colors";
  return (
    <div className="mt-[22px]">
      <div
        aria-hidden="true"
        className="mb-3.5 h-4"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg,var(--dash) 0 1px,transparent 1px 9px)",
          WebkitMaskImage:
            "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
          maskImage:
            "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
        }}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-[13px] text-[color:var(--text-muted)]">
          {t(keys.orders.pageInfo, { shown: String(shown), total: String(total) })}
        </span>
        {totalPages >= 1 ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => onPage(page - 1)}
              aria-label={t(keys.common.previous)}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronPrev />
            </button>
            {buildPageList(page, totalPages).map((p, i) =>
              p === "…" ? (
                <span
                  key={`gap-${i}`}
                  className="px-1 text-[13px] text-[color:var(--text-muted)]"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPage(p)}
                  aria-current={p === page ? "page" : undefined}
                  className={
                    p === page
                      ? `${pageBtn} bg-[color:var(--accent-500)] text-[color:var(--on-accent)]`
                      : `${pageBtn} border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[color:var(--text-secondary)] hover:bg-[color:var(--soft)]`
                  }
                >
                  {p}
                </button>
              ),
            )}
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => onPage(page + 1)}
              aria-label={t(keys.common.next)}
              className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--card)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)] disabled:pointer-events-none disabled:opacity-40"
            >
              <ChevronNext />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ChevronPrev() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronNext() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
